import { lazy, Suspense, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const Calendar = lazy(() => import("@/components/ui/calendar").then((module) => ({ default: module.Calendar })));

interface DatePickerDialogProps {
  mobile?: boolean;
  navigation?: boolean;
}

const DatePickerDialog = ({ mobile = false, navigation = false }: DatePickerDialogProps) => {
  const [date, setDate] = useState<Date>();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setDate(selectedDate);
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      setOpen(false);
      navigate(`/resultados/${formattedDate}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {mobile || navigation ? (
          <Button variant="ghost" className={cn("justify-start", mobile && "text-lg")}>
            Resultados por fecha
          </Button>
        ) : (
          <Button variant="outline" size="lg" className="gap-2 px-3 md:px-8">
            <CalendarIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Resultados por Fecha</span>
            <span className="sm:hidden">Por Fecha</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Selecciona una fecha</DialogTitle>
          <DialogDescription>
            Elige una fecha para ver los resultados históricos de Nicaragua y Costa Rica
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center py-4">
          <Suspense fallback={<div className="h-80 w-64 animate-pulse rounded-md bg-muted" />}>
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            disabled={(date) => date > new Date() || date < new Date("2024-01-01")}
            initialFocus
            locale={es}
            className={cn("p-3 pointer-events-auto rounded-md border")}
          />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DatePickerDialog;
